import { useEffect } from 'react';

/**
 * Lightweight head manager without extra deps.
 * Updates title, meta description, robots, and canonical link for SEO.
 */
const SeoHead = ({
  title,
  description,
  canonicalPath,
  robots = 'index,follow',
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (title) {
      document.title = title;
    }

    const ensureMeta = (name, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const ensureMetaProperty = (property, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    ensureMeta('description', description);
    ensureMeta('robots', robots);
    ensureMeta('twitter:card', twitterCard);
    ensureMeta('twitter:title', ogTitle || title);
    ensureMeta('twitter:description', ogDescription || description);
    if (ogImage) ensureMeta('twitter:image', ogImage);

    ensureMetaProperty('og:title', ogTitle || title);
    ensureMetaProperty('og:description', ogDescription || description);
    ensureMetaProperty('og:type', ogType);

    if (canonicalPath) {
      const siteOrigin =
        (process.env.REACT_APP_SITE_URL || '').trim() ||
        (typeof window !== 'undefined' ? window.location.origin : '');
      if (siteOrigin) {
        const canonicalUrl = `${siteOrigin}${canonicalPath}`;
        let linkTag = document.querySelector('link[rel="canonical"]');
        if (!linkTag) {
          linkTag = document.createElement('link');
          linkTag.setAttribute('rel', 'canonical');
          document.head.appendChild(linkTag);
        }
        linkTag.setAttribute('href', canonicalUrl);
        ensureMetaProperty('og:url', canonicalUrl);
      }
    }
  }, [
    title,
    description,
    canonicalPath,
    robots,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    twitterCard,
  ]);

  return null;
};

export default SeoHead;
